import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';

interface TrendDataPoint {
    year: number;
    value: number;
}

interface D3TrendChartProps {
    data: TrendDataPoint[];
    selectedRatio: string;
    width?: number;
    height?: number;
}

const D3TrendChart: React.FC<D3TrendChartProps> = ({
    data,
    selectedRatio,
    width = 800,
    height = 400
}) => {
    const svgRef = useRef<SVGSVGElement>(null);

    useEffect(() => {
        if (!svgRef.current || data.length === 0) return;

        // Clear previous content
        d3.select(svgRef.current).selectAll('*').remove();

        const svg = d3.select(svgRef.current);
        const margin = { top: 30, right: 50, bottom: 60, left: 80 };
        const chartWidth = width - margin.left - margin.right;
        const chartHeight = height - margin.top - margin.bottom;

        // Create the main group
        const g = svg.append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        // Set up scales
        const yearDomain = d3.extent(data, d => d.year) as [number, number];
        const yearPadding = (yearDomain[1] - yearDomain[0]) * 0.15; // 15% padding on each side

        const xScale = d3.scaleLinear()
            .domain([yearDomain[0] - yearPadding, yearDomain[1] + yearPadding])
            .range([0, chartWidth]);

        const yScale = d3.scaleLinear()
            .domain([0, d3.max(data, d => d.value)! * 1.1] as [number, number])
            .range([chartHeight, 0]);

        // Generate integer year ticks for x-axis
        const integerYearTicks = d3.range(Math.floor(yearDomain[0]), Math.ceil(yearDomain[1]) + 1);

        // Create line generator
        const line = d3.line<TrendDataPoint>()
            .x(d => xScale(d.year))
            .y(d => yScale(d.value))
            .curve(d3.curveMonotoneX);

        // Add X axis
        g.append('g')
            .attr('transform', `translate(0,${chartHeight})`)
            .call(d3.axisBottom(xScale)
                .tickValues(integerYearTicks)
                .tickFormat(d => d.toString()))
            .append('text')
            .attr('fill', '#000')
            .attr('x', chartWidth / 2)
            .attr('y', 50)
            .attr('text-anchor', 'middle')
            .style('font-size', '14px')
            .style('font-weight', 'bold')
            .text('Year');

        // Add the line
        g.append('path')
            .datum(data)
            .attr('fill', 'none')
            .attr('stroke', '#3b82f6')
            .attr('stroke-width', 3)
            .attr('d', line);

        // Add data points
        g.selectAll('.dot')
            .data(data)
            .enter()
            .append('circle')
            .attr('class', 'dot')
            .attr('cx', d => xScale(d.year))
            .attr('cy', d => yScale(d.value))
            .attr('r', 6)
            .attr('fill', '#3b82f6')
            .attr('stroke', '#fff')
            .attr('stroke-width', 2);

        // Add value labels
        g.selectAll('.label')
            .data(data)
            .enter()
            .append('text')
            .attr('class', 'label')
            .attr('x', d => xScale(d.year))
            .attr('y', d => yScale(d.value) - 10)
            .attr('text-anchor', 'middle')
            .style('font-size', '12px')
            .style('font-weight', 'bold')
            .text(d => d.value.toFixed(2));

        // Add tooltip
        const tooltip = d3.select('body')
            .append('div')
            .style('position', 'absolute')
            .style('background', 'rgba(0, 0, 0, 0.8)')
            .style('color', 'white')
            .style('padding', '8px')
            .style('border-radius', '4px')
            .style('pointer-events', 'none')
            .style('opacity', 0)
            .style('font-size', '12px');

        g.selectAll('.dot')
            .on('mouseover', function (event, d) {
                const year = (d as TrendDataPoint).year;
                const value = (d as TrendDataPoint).value;
                return tooltip
                    .style('opacity', 1)
                    .html(`Year: ${year}<br/>Value: ${value.toFixed(2)}`);
            })
            .on('mousemove', function (event) {
                tooltip
                    .style('left', (event.pageX + 10) + 'px')
                    .style('top', (event.pageY - 10) + 'px');
            })
            .on('mouseout', function () {
                tooltip.style('opacity', 0);
            });

        // Cleanup tooltip on unmount
        return () => {
            tooltip.remove();
        };
    }, [data, selectedRatio, width, height]);

    if (data.length === 0) {
        return (
            <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <div className="text-center">
                    <p className="text-gray-500 text-lg">No trend data available</p>
                    <p className="text-gray-400 text-sm mt-2">
                        Upload financial statements for multiple years to see trends
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full overflow-x-auto">
            <svg ref={svgRef} width={width} height={height}></svg>
        </div>
    );
};

export default D3TrendChart;

